import type { FocusEvent } from "react"
import { Controller, type Control, type FieldErrors, type UseFormRegister } from "react-hook-form"
import { Car, ChevronsUpDown, FileText } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn, formatarMoedaInput, parsearMoeda } from "@/lib/utils"
import { filterBrands, vehicleBrands } from "@/lib/brandsData"
import { DocumentUpload } from "@/components/DocumentUpload"
import PhotoUploadPrincipal from "@/components/PhotoUploadPrincipal"
import type { UploadableDocument, UploadablePhoto } from "@/lib/vehicleStorage"

type FormApi = {
  register: UseFormRegister<any>
  control: Control<any>
  errors: FieldErrors<any>
}

interface VeiculoFormSectionsProps extends FormApi {
  years: number[]
  documents: UploadableDocument[]
  onDocumentsUpdate: (documents: UploadableDocument[]) => void
  photo?: UploadablePhoto | string | null
  onPhotoUpdate?: (photo: UploadablePhoto | string | null) => void
  disabled?: boolean
  brandOpen: boolean
  setBrandOpen: (open: boolean) => void
  brandSearchValue: string
  setBrandSearchValue: (value: string) => void
  showPhoto?: boolean
  showDocuments?: boolean
  showValorFIPE?: boolean
  identificationTitle?: string
  identificationDescription?: string
  valorFIPEHelperText?: string
  onAnyInteraction?: () => void
}

export function VeiculoFormSections({
  register,
  control,
  errors,
  years,
  documents,
  onDocumentsUpdate,
  photo,
  onPhotoUpdate,
  disabled = false,
  brandOpen,
  setBrandOpen,
  brandSearchValue,
  setBrandSearchValue,
  showPhoto = true,
  showDocuments = true,
  showValorFIPE = true,
  identificationTitle = "Identificação do Veículo",
  identificationDescription = "Informações básicas para identificação do veículo",
  valorFIPEHelperText = "Preencha manualmente se a consulta automática não retornar FIPE.",
  onAnyInteraction,
}: VeiculoFormSectionsProps) {
  const marcaField = (
    <div className="space-y-2">
      <Label htmlFor="marca">Marca *</Label>
      <Controller
        name="marca"
        control={control}
        rules={{ required: "Marca é obrigatória" }}
        render={({ field }) => {
          const filteredBrands = filterBrands(brandSearchValue)

          return (
            <Popover open={brandOpen} onOpenChange={setBrandOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={brandOpen}
                  className={cn(
                    "w-full justify-between",
                    errors.marca && "border-destructive",
                    !field.value && "text-muted-foreground"
                  )}
                >
                  {field.value ? vehicleBrands.find((brand) => brand === field.value) : "Selecione ou digite a marca"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder="Buscar marca..."
                    value={brandSearchValue}
                    onValueChange={setBrandSearchValue}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {brandSearchValue ? (
                        <div className="py-6 text-center text-sm">
                          <p>Marca não encontrada</p>
                          <p className="text-muted-foreground">Use "{brandSearchValue}" como marca personalizada</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                              field.onChange(brandSearchValue)
                              setBrandOpen(false)
                              setBrandSearchValue("")
                            }}
                          >
                            Usar "{brandSearchValue}"
                          </Button>
                        </div>
                      ) : (
                        "Nenhuma marca encontrada."
                      )}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredBrands.map((brand) => (
                        <CommandItem
                          key={brand}
                          value={brand}
                          onSelect={() => {
                            field.onChange(brand)
                            setBrandOpen(false)
                            setBrandSearchValue("")
                          }}
                        >
                          {brand}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )
        }}
      />
      {errors.marca && <p className="text-sm text-destructive">{String(errors.marca.message || "Marca é obrigatória")}</p>}
    </div>
  )

  return (
    <div
      className="space-y-6"
      onChangeCapture={onAnyInteraction}
      onClickCapture={(event) => {
        if ((event.target as HTMLElement).closest('[role="combobox"], [role="option"]')) {
          onAnyInteraction?.()
        }
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            {identificationTitle}
          </CardTitle>
          <CardDescription>{identificationDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="placa">Placa *</Label>
              <Input
                id="placa"
                placeholder="ABC1234"
                {...register("placa", { required: "Placa é obrigatória" })}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
                  void register("placa").onChange(e)
                }}
                maxLength={7}
                className={errors.placa ? "border-destructive" : ""}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              {errors.placa && <p className="text-sm text-destructive">{String(errors.placa.message)}</p>}
            </div>
            {marcaField}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="modelo">Modelo *</Label>
              <Input
                id="modelo"
                placeholder="Ex: Civic, Corolla, Golf"
                {...register("modelo", { required: "Modelo é obrigatório" })}
                className={errors.modelo ? "border-destructive" : ""}
              />
              {errors.modelo && <p className="text-sm text-destructive">{String(errors.modelo.message)}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="versao">Versão</Label>
              <Input id="versao" placeholder="Ex: EXL, LTZ, XEi" {...register("versao")} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ano">Ano de Fabricação *</Label>
              <Controller
                name="ano"
                control={control}
                rules={{ required: "Ano de fabricação é obrigatório" }}
                render={({ field }) => (
                  <Select value={field.value || ""} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.ano ? "border-destructive" : ""}>
                      <SelectValue placeholder="Selecione o ano" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.ano && <p className="text-sm text-destructive">{String(errors.ano.message || "Ano de fabricação é obrigatório")}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="anoModelo">Ano do Modelo</Label>
              <Input id="anoModelo" placeholder="Ex: 2025" {...register("anoModelo")} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="chassi">Chassi</Label>
              <Input id="chassi" placeholder="Ex: 1HGBH41JXMN109186" {...register("chassi")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="renavam">RENAVAM</Label>
              <Input id="renavam" placeholder="Ex: 12345678901" {...register("renavam")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {showPhoto && onPhotoUpdate && (
        <Card>
          <CardHeader>
            <CardTitle>Foto do Veículo</CardTitle>
            <CardDescription>Adicione uma foto principal para reconhecer o veículo com facilidade.</CardDescription>
          </CardHeader>
          <CardContent>
            <PhotoUploadPrincipal photo={photo ?? null} onPhotoUpdate={onPhotoUpdate} disabled={disabled} />
          </CardContent>
        </Card>
      )}

      {showDocuments && (
        <Card id="documentos-veiculo">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documentação do Veículo
            </CardTitle>
            <CardDescription>Faça upload dos documentos do veículo (CRLV, laudos, etc.)</CardDescription>
          </CardHeader>
          <CardContent>
            <DocumentUpload documents={documents} onDocumentsUpdate={onDocumentsUpdate} maxDocuments={10} disabled={disabled} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Informações Adicionais</CardTitle>
          <CardDescription>Dados complementares do veículo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="potencia">Potência</Label>
              <Input id="potencia" placeholder="Ex: 120 cv" {...register("potencia")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacidadeCarga">Capacidade de Carga (kg)</Label>
              <Input id="capacidadeCarga" placeholder="Ex: 500" type="number" {...register("capacidadeCarga")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passageiros">Passageiros</Label>
              <Input id="passageiros" placeholder="Ex: 5" type="number" {...register("passageiros")} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="tipoVeiculo">Tipo de Veículo</Label>
              <Input id="tipoVeiculo" placeholder="Ex: SUV, Sedan" {...register("tipoVeiculo")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input id="cidade" placeholder="Ex: São Paulo" {...register("cidade")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Input id="estado" placeholder="Ex: SP" maxLength={2} {...register("estado")} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="situacaoVeiculo">Situação do Veículo</Label>
              <Input id="situacaoVeiculo" placeholder="Ex: Regular, Roubado, Irregular" {...register("situacaoVeiculo")} />
            </div>
            {showValorFIPE && (
              <div className="space-y-2">
                <Label htmlFor="valorFIPE">Valor FIPE</Label>
                <Input
                  id="valorFIPE"
                  placeholder="Ex: R$ 85.000,00"
                  {...register("valorFIPE", {
                    onBlur: (event: FocusEvent<HTMLInputElement>) => {
                      const valor = parsearMoeda(event.target.value)
                      event.target.value = valor > 0 ? formatarMoedaInput(valor) : ""
                    },
                  })}
                />
                <p className="text-xs text-muted-foreground">{valorFIPEHelperText}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Características Técnicas</CardTitle>
          <CardDescription>Especificações técnicas do veículo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="cor">Cor</Label>
              <Controller
                name="cor"
                control={control}
                render={({ field }) => (
                  <Select value={field.value || ""} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="branco">Branco</SelectItem>
                      <SelectItem value="preto">Preto</SelectItem>
                      <SelectItem value="prata">Prata</SelectItem>
                      <SelectItem value="cinza">Cinza</SelectItem>
                      <SelectItem value="azul">Azul</SelectItem>
                      <SelectItem value="vermelho">Vermelho</SelectItem>
                      <SelectItem value="verde">Verde</SelectItem>
                      <SelectItem value="amarelo">Amarelo</SelectItem>
                      <SelectItem value="marrom">Marrom</SelectItem>
                      <SelectItem value="dourado">Dourado</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="combustivel">Combustível</Label>
              <Input
                id="combustivel"
                placeholder="Ex: Gasolina, Etanol, Flex, Diesel, GNV, Elétrico, Híbrido, Álcool/Gasolina"
                {...register("combustivel")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cambio">Transmissão</Label>
              <Controller
                name="cambio"
                control={control}
                render={({ field }) => (
                  <Select value={field.value || ""} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatica">Automática</SelectItem>
                      <SelectItem value="cvt">CVT</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="quilometragem">Quilometragem (km)</Label>
              <Input id="quilometragem" placeholder="0" type="number" {...register("quilometragem")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="motor">Motor</Label>
              <Input id="motor" placeholder="Ex: 1.0, 1.6, 2.0" {...register("motor")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portas">Número de Portas</Label>
              <Controller
                name="portas"
                control={control}
                render={({ field }) => (
                  <Select value={field.value || ""} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 portas</SelectItem>
                      <SelectItem value="3">3 portas</SelectItem>
                      <SelectItem value="4">4 portas</SelectItem>
                      <SelectItem value="5">5 portas</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Observações Adicionais</CardTitle>
          <CardDescription>Informações extras sobre o veículo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              placeholder="Adicione informações relevantes sobre o veículo..."
              className="min-h-[100px]"
              {...register("observacoes")}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
